/**
 * Shared color utility functions used across components
 */

/** Parses a hex color string into { r, g, b } components */
export const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const c = hex.replace('#', '');

  return {
    r: parseInt(c.slice(0, 2), 16),
    g: parseInt(c.slice(2, 4), 16),
    b: parseInt(c.slice(4, 6), 16),
  };
};

/**
 * Converts RGB object to hex color string
 */
export const rgbToHex = (rgb: { r: number; g: number; b: number }): string =>
  `#${[rgb.r, rgb.g, rgb.b]
    .map((x) => x.toString(16).padStart(2, '0'))
    .join('')}`;

/**
 * Validates if a string is a valid hex color
 */
export const isValidHexColor = (hex: string): boolean =>
  /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/.test(hex);

/**
 * Linearly interpolates between two hex colors.
 * t=0 returns colorA, t=1 returns colorB.
 */
export const lerpHex = (colorA: string, colorB: string, t: number): string => {
  const rgb1 = hexToRgb(colorA);
  const rgb2 = hexToRgb(colorB);

  return `rgb(${Math.round(rgb1.r + (rgb2.r - rgb1.r) * t)},${Math.round(
    rgb1.g + (rgb2.g - rgb1.g) * t
  )},${Math.round(rgb1.b + (rgb2.b - rgb1.b) * t)})`;
};

/**
 * Interpolates between multiple color stops.
 * colorStops: array of [position (0-1), color] pairs
 * t: interpolation value (0-1)
 * Example: [[0, '#FF0000'], [0.5, '#00FF00'], [1, '#0000FF']]
 */
export const lerpMultipleColors = (
  colorStops: [number, string][],
  t: number
): string => {
  // Clamp t between 0 and 1
  const clampedT = Math.max(0, Math.min(1, t));

  // Find the two stops that t falls between
  for (let i = 0; i < colorStops.length - 1; i += 1) {
    const [pos1, color1] = colorStops[i];
    const [pos2, color2] = colorStops[i + 1];

    if (clampedT >= pos1 && clampedT <= pos2) {
      // Interpolate between these two colors
      const localT = (clampedT - pos1) / (pos2 - pos1);

      return lerpHex(color1, color2, localT);
    }
  }

  // Fallback to last color
  return colorStops[colorStops.length - 1][1];
};
